/**
 * Live-Suche in der Kundendatenbank – die eine Stelle für dieses Verhalten.
 *
 * Wird an zwei Orten gebraucht: beim Anlegen eines Angebots und beim Anlegen
 * eines Kunden. Beide sollen sich gleich anfühlen, deshalb liegt die Logik
 * hier und nicht zweimal nebeneinander. Wer die Suche ändert, ändert sie
 * überall.
 *
 * Bedienung wie gewohnt aus solchen Feldern:
 *   ab 2 Zeichen wird gesucht, verzögert um 250 ms
 *   Pfeiltasten wählen, Enter übernimmt, Escape blendet aus
 *   das Getippte steht fett im Namen
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { api } from '../api';
import type { Kunde, KundenTreffer } from '../typen';
import './kundensuche.css';

/** Ab wie vielen Zeichen gesucht wird. Darunter zeigt die Liste nichts. */
export const SUCHE_AB = 2;

export interface Kundensuche {
  vorschlaege: KundenTreffer[];
  /** Index des mit der Tastatur gewählten Eintrags. */
  index: number;
  setIndex: (i: number) => void;
  /** Sichtbar? Zusammengesetzt aus Eingabelänge, offenem Zustand und Auswahl. */
  offen: boolean;
  schliessen: () => void;
  oeffnen: () => void;
  /**
   * In `onKeyDown` des Eingabefelds hängen.
   *
   * `onNeu` ist der Anlegen-Eintrag am Ende der Liste. Ist er gesetzt, zählt er
   * als zusätzliche Zeile: Pfeiltasten laufen darüber, Enter löst ihn aus. Bei
   * null Treffern steht er allein da und ist damit vorausgewählt – dann genügt
   * Enter, um einen neuen Kunden anzulegen.
   */
  taste: (e: KeyboardEvent, onWaehlen: (k: Kunde) => void, onNeu?: () => void) => void;
}

/**
 * @param wert     der eingetippte Text
 * @param gesperrt true, wenn bereits ein Kunde gewählt ist – dann ruht die Suche
 */
export function useKundensuche(wert: string, gesperrt = false): Kundensuche {
  const [vorschlaege, setVorschlaege] = useState<KundenTreffer[]>([]);
  const [index, setIndex] = useState(0);
  /*
   * Startwert bewusst false: Die Liste erscheint erst, wenn jemand tippt.
   * Sonst springt sie auf, sobald ein Feld vorbelegt ist - etwa wenn aus der
   * Suche heraus ein neuer Kunde angelegt wird und der Name schon im
   * Formular steht. Sie liegt dann ueber dem Fenster und sieht aus wie ein
   * haengengebliebenes Dropdown. Beide Aufrufstellen oeffnen bei Eingabe.
   */
  const [sichtbar, setSichtbar] = useState(false);

  const lang = wert.trim().length >= SUCHE_AB;
  const offen = sichtbar && !gesperrt && lang;

  useEffect(() => {
    if (gesperrt || !lang || !sichtbar) {
      setVorschlaege([]);
      return;
    }
    const zeitgeber = window.setTimeout(() => {
      void api
        .hole<KundenTreffer[]>(`/kunden/suche?q=${encodeURIComponent(wert.trim())}`)
        .then((t) => {
          setVorschlaege(t);
          setIndex(0);
        })
        .catch(() => setVorschlaege([]));
    }, 250);
    return () => window.clearTimeout(zeitgeber);
  }, [wert, gesperrt, lang, sichtbar]);

  function taste(e: KeyboardEvent, onWaehlen: (k: Kunde) => void, onNeu?: () => void) {
    if (!offen) return;
    const zeilen = vorschlaege.length + (onNeu ? 1 : 0);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex(zeilen ? (index + 1) % zeilen : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex(zeilen ? (index - 1 + zeilen) % zeilen : 0);
    } else if (e.key === 'Enter') {
      const treffer = vorschlaege[index];
      if (treffer) {
        e.preventDefault();
        onWaehlen(treffer.kunde);
      } else if (onNeu && index === vorschlaege.length) {
        e.preventDefault();
        // Erst schließen, dann auslösen: Sonst bleibt die Liste über der
        // Kundenmaske stehen und muss von Hand weggeklickt werden.
        setSichtbar(false);
        onNeu();
      }
    } else if (e.key === 'Escape') {
      setSichtbar(false);
    }
  }

  return {
    vorschlaege,
    index,
    setIndex,
    offen,
    schliessen: () => setSichtbar(false),
    oeffnen: () => setSichtbar(true),
    taste,
  };
}

/**
 * Hebt den getippten Text im Namen hervor – rein optisch. Gefunden wird am
 * Server, hier wird nur sichtbar gemacht, warum ein Eintrag in der Liste steht.
 */
export function Hervorgehoben({ text, suche }: { text: string; suche: string }) {
  const nadel = suche.trim();
  if (nadel.length < SUCHE_AB) return <>{text}</>;

  const stelle = text.toLocaleLowerCase('de-AT').indexOf(nadel.toLocaleLowerCase('de-AT'));
  if (stelle < 0) return <>{text}</>;

  return (
    <>
      {text.slice(0, stelle)}
      <mark className="ks-treffer">{text.slice(stelle, stelle + nadel.length)}</mark>
      {text.slice(stelle + nadel.length)}
    </>
  );
}

/** Wohin die Liste gehört – berechnet aus dem umgebenden `.ks-feld`. */
interface Platz {
  links: number;
  breite: number;
  /** Oberkante, wenn die Liste unter dem Feld steht. */
  oben: number | null;
  /** Abstand vom unteren Rand, wenn sie nach oben kippt. */
  unten: number | null;
  maxHoehe: number;
}

const ABSTAND = 6;
const RAND = 12;
/** Darunter lohnt sich die Liste nach unten nicht mehr – dann kippt sie. */
const MINDESTHOEHE = 150;

/**
 * Misst das Feld, an dem die Liste hängt.
 *
 * Die Liste liegt bewusst nicht im Fluss, sondern per Portal am Dokument:
 * In der Kundenmaske scrollt der Modalkörper, und eine eingebettete Liste
 * würde dort abgeschnitten. So verhält sie sich auf jeder Seite gleich.
 */
function usePlatz(marker: { current: HTMLElement | null }, aktiv: boolean): Platz | null {
  const [platz, setPlatz] = useState<Platz | null>(null);

  useLayoutEffect(() => {
    if (!aktiv) {
      setPlatz(null);
      return;
    }
    const feld = marker.current?.closest('.ks-feld');
    if (!feld) return;

    function messen() {
      const k = (feld as HTMLElement).getBoundingClientRect();
      const darunter = window.innerHeight - k.bottom - ABSTAND - RAND;
      const darueber = k.top - ABSTAND - RAND;
      const nachOben = darunter < MINDESTHOEHE && darueber > darunter;
      setPlatz({
        links: Math.round(k.left),
        breite: Math.round(k.width),
        oben: nachOben ? null : Math.round(k.bottom + ABSTAND),
        unten: nachOben ? Math.round(window.innerHeight - k.top + ABSTAND) : null,
        maxHoehe: Math.max(MINDESTHOEHE, Math.round(nachOben ? darueber : darunter)),
      });
    }

    messen();
    // Beim Scrollen im Modalkörper muss die Liste mitwandern – deshalb in der
    // Erfassungsphase, sonst bekommen wir das innere Scrollen nicht mit.
    window.addEventListener('scroll', messen, true);
    window.addEventListener('resize', messen);
    return () => {
      window.removeEventListener('scroll', messen, true);
      window.removeEventListener('resize', messen);
    };
  }, [marker, aktiv]);

  return platz;
}

/** Die Vorschlagsliste. */
export function Kundenvorschlaege({
  suche,
  suchstand,
  onWaehlen,
  zusatz,
  neuAktion,
}: {
  suche: string;
  suchstand: Kundensuche;
  onWaehlen: (k: Kunde) => void;
  /** Zusätzliche Angabe rechts, z. B. die Anzahl vorhandener Angebote. */
  zusatz?: (k: Kunde) => ReactNode;
  /**
   * Letzter Eintrag der Liste: „Neuen Kunden anlegen".
   *
   * Steht immer zur Verfügung, nicht nur bei null Treffern – manchmal gibt es
   * einen ähnlichen Namen und trotzdem einen neuen Kunden. Bei null Treffern
   * ist er der einzige Eintrag und damit vorausgewählt.
   */
  neuAktion?: { label: string; hinweis?: string; onAuswahl: () => void };
}) {
  const { vorschlaege, index, setIndex, offen, schliessen } = suchstand;
  const marker = useRef<HTMLSpanElement>(null);
  const platz = usePlatz(marker, offen);

  // Der Marker bleibt im Fluss stehen und sagt der Liste, wo ihr Feld sitzt.
  if (!offen) return <span ref={marker} hidden />;

  const liste = platz && (
    <div
      className="ks-liste"
      role="listbox"
      style={{
        left: platz.links,
        width: platz.breite,
        top: platz.oben ?? undefined,
        bottom: platz.unten ?? undefined,
        maxHeight: platz.maxHoehe,
      }}
    >
      <div className="ks-kopf">
        <span>
          Kundendatenbank ·{' '}
          {vorschlaege.length === 0
            ? 'kein Treffer'
            : vorschlaege.length === 1
              ? '1 Treffer'
              : `${vorschlaege.length} Treffer`}
        </span>
        <button type="button" title="Vorschläge ausblenden" onClick={schliessen}>
          ✕
        </button>
      </div>

      {vorschlaege.map((t, i) => (
        <button
          key={t.kunde.id}
          type="button"
          role="option"
          aria-selected={i === index}
          className={`ks-eintrag ${i === index ? 'aktiv' : ''}`}
          onMouseEnter={() => setIndex(i)}
          onClick={() => {
            schliessen();
            onWaehlen(t.kunde);
          }}
        >
          <span className="ks-symbol">
            {t.kunde.typ === 'organisation' ? (
              <span className="kunden-symbol-org" />
            ) : (
              <span className="kunden-symbol-person">
                <span />
              </span>
            )}
          </span>
          <span className="ks-text">
            <span className="ks-name">
              <Hervorgehoben text={t.kunde.name} suche={suche} />
            </span>
            <span className="ks-meta">
              Nr. {t.kunde.kundenNr} · {t.kunde.ortZeile} · {t.kunde.mail || '—'}
            </span>
          </span>
          <span className="ks-rechts">
            {zusatz?.(t.kunde)}
            <span className="ks-grund">Treffer: {t.grund}</span>
          </span>
        </button>
      ))}

      {neuAktion && (
        <button
          type="button"
          role="option"
          aria-selected={index === vorschlaege.length}
          className={`ks-eintrag ks-neu ${index === vorschlaege.length ? 'aktiv' : ''}`}
          onMouseEnter={() => setIndex(vorschlaege.length)}
          onClick={() => {
            schliessen();
            neuAktion.onAuswahl();
          }}
        >
          <span className="ks-symbol ks-plus">+</span>
          <span className="ks-text">
            <span className="ks-name">{neuAktion.label}</span>
            {neuAktion.hinweis && <span className="ks-meta">{neuAktion.hinweis}</span>}
          </span>
          <span className="ks-rechts">
            <span className="ks-grund">Enter</span>
          </span>
        </button>
      )}
    </div>
  );

  return (
    <>
      <span ref={marker} hidden />
      {liste && createPortal(liste, document.body)}
    </>
  );
}
